import { useEffect, useRef } from 'react';
import { useRevalidator, useSearchParams } from 'react-router';
import { postProposalPaymentConfirm } from '../util/proposal-payment-confirm';
import { PAYMENT_SUCCESS_MESSAGE } from '../type/types';
const STRIPE_RETURN_QUERY_KEYS = [
    'setup_intent',
    'setup_intent_client_secret',
    'payment_intent',
    'payment_intent_client_secret',
    'redirect_status',
    'invoice_payment_id',
    'intent_type',
] as const;

/**
 * One-shot server confirm when Stripe 3DS redirects back to the proposal page.
 */
export const useProposalStripeReturnConfirm = (options: {
    proposalUrlSegment: string;
    disabled?: boolean;
    toast: (message: string, opts?: { type?: 'error' | 'success' }) => void;
    onConfirmFallback: () => void;
}) => {
    const { disabled, onConfirmFallback, proposalUrlSegment, toast } = options;
    const [searchParams] = useSearchParams();
    const revalidator = useRevalidator();
    const startedRef = useRef(false);

    useEffect(() => {
        if (disabled) {
            return;
        }

        if (searchParams.get('redirect_status') !== 'succeeded') {
            return;
        }

        const invoicePaymentId = searchParams.get('invoice_payment_id');
        const intentType = searchParams.get('intent_type');
        const setupIntentId = searchParams.get('setup_intent');
        const paymentIntentId = searchParams.get('payment_intent');

        if (!invoicePaymentId) {
            return;
        }

        if (intentType !== 'setup' && intentType !== 'payment') {
            return;
        }

        const stripeIntentId = setupIntentId || paymentIntentId;
        if (!stripeIntentId) {
            return;
        }

        if (startedRef.current) {
            return;
        }
        startedRef.current = true;

        const cleanReturnParams = () => {
            const url = new URL(window.location.href);
            for (const key of STRIPE_RETURN_QUERY_KEYS) {
                url.searchParams.delete(key);
            }
            window.history.replaceState({}, '', `${url.pathname}${url.search}`);
        };

        void (async () => {
            const confirmResult = await postProposalPaymentConfirm({
                proposalUrlSegment,
                invoicePaymentId,
                intentType,
                stripeIntentId,
            });

            cleanReturnParams();

            if (
                confirmResult.ok &&
                (confirmResult.outcome === 'paid' ||
                    confirmResult.outcome === 'already_paid')
            ) {
                revalidator.revalidate();
                toast(PAYMENT_SUCCESS_MESSAGE);
                return;
            }

            if (!confirmResult.ok && confirmResult.retryable) {
                onConfirmFallback();
                toast(
                    'We are still confirming your payment. This may take a moment.',
                );
                return;
            }

            if (!confirmResult.ok) {
                toast(confirmResult.message, { type: 'error' });
            }
        })();
    }, [
        searchParams,
        disabled,
        proposalUrlSegment,
        toast,
        onConfirmFallback,
        revalidator,
    ]);
};
